import defaultAxios, { AxiosError } from "axios";
import { create } from "zustand";
import Swal from "sweetalert2";
import axios from "../services/axios";
import { queryClient } from "../config/queryClient";
import {
    LoginBody,
    LoginResponse,
    LogoutResponse,
    RegistrasiForm,
    RegistrasiResponse,
    UserResponse,
} from "../constants/types";
import { getLocalUkmId, setLocalUkmId } from "../utils/handleChangeUkmId";

export interface AuthStore {
    isAuthenticated: boolean;
    isRegistered: boolean;
    isPreparingApp: boolean;
    isLoading: boolean;
    ukmIdUser?: number[];
    loginCount: number;
    checkToken: () => Promise<void>;
    loadResourcesAndDataAsync: () => Promise<void>;
    login: (values: LoginBody) => Promise<void>;
    logout: () => Promise<void>;
    register: (values: RegistrasiForm) => Promise<void>;
    handleRegistered: () => void;
    setRegistered: (value: boolean) => void;
    handleAuthenticated: (value: boolean) => void;
    resetLoginCount: () => void;
}

const initialLoginCount = Number(localStorage.getItem("loginCount")) || 0;

const handleErrorResponse = (
    error: unknown,
    loginCount: number,
    setLoginCount: (n: number) => void,
) => {
    if (defaultAxios.isAxiosError(error)) {
        const serverError = error as AxiosError<any>;
        if (serverError && serverError.response) {
            console.log(`serverError`, serverError.response);
            if (loginCount >= 5) {
                Swal.fire({
                    title: "Batas upaya masuk tercapai!",
                    text: `Anda harus menunggu 5 menit sebelum bisa mencoba masuk kembali`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: { container: "my-swal" },
                });
            } else if (serverError.response!.status === 400) {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    text: `${serverError.response.data.data.errors.message}`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: { container: "my-swal" },
                });
                const jumlahGagal = serverError.response.data.data.jumlah_gagal;
                setLoginCount(jumlahGagal);
                localStorage.setItem("loginCount", String(jumlahGagal));
            }

            if (serverError.response!.status === 422) {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    text: `Ada error validasi`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: { container: "my-swal" },
                });
            }

            if (serverError.response!.status === 403) {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    text: `${serverError.response.data.message}`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: { container: "my-swal" },
                });
            }
        } else {
            console.error(
                "",
                `Terjadi kesalahan! Silahkan coba lagi.`,
                [{ text: "OK" }],
            );
        }
    }
};

export const useAuthStore = create<AuthStore>()((set, get) => ({
    isAuthenticated: false,
    isRegistered: false,
    isPreparingApp: true,
    isLoading: false,
    ukmIdUser: undefined,
    loginCount: initialLoginCount,
    checkToken: async () => {
        try {
            const { data: user } = await axios.get<UserResponse>("/api/users");
            const ukmId = getLocalUkmId(user?.data?.userId);
            if (ukmId) {
                set({ ukmIdUser: [ukmId] });
            } else {
                set({ ukmIdUser: user.data.ukmId });
                setLocalUkmId(user.data.userId, user.data.ukmId[0]);
            }
            set({ isAuthenticated: true });
        } catch (error) {
            handleErrorResponse(error, get().loginCount, (n) =>
                set({ loginCount: n }),
            );
            set({ isAuthenticated: false });
            queryClient.clear();
        }
    },
    loadResourcesAndDataAsync: async () => {
        try {
            await get().checkToken();
        } catch (e) {
            console.warn(e);
        } finally {
            set({ isPreparingApp: false });
        }
    },
    login: async (values) => {
        set({ isLoading: true });
        try {
            const { data } = await axios.post<LoginResponse>("/api/login", {
                ...values,
            });
            console.log(`data`, data);
            const { data: user } = await axios.get<UserResponse>("/api/users");
            const ukmId = getLocalUkmId(user?.data?.userId);
            if (ukmId) {
                set({ ukmIdUser: [ukmId] });
            } else {
                set({ ukmIdUser: user.data.ukmId });
                setLocalUkmId(user.data.userId, user.data.ukmId[0]);
            }
            if (data.code === 200) {
                set({ isAuthenticated: true });
                localStorage.removeItem("loginCount");
            }
            set({ isLoading: false });
        } catch (error) {
            set({ isLoading: false });
            handleErrorResponse(error, get().loginCount, (n) =>
                set({ loginCount: n }),
            );
        }
    },
    register: async (values) => {
        set({ isLoading: true });
        try {
            const formData = new FormData();
            formData.append("nama", values.nama);
            formData.append("username", values.username);
            formData.append("email", values.email);
            formData.append("no_telp", values.no_telp);
            formData.append("password", values.password);
            formData.append("role", "10");
            const { data } = await axios.post<RegistrasiResponse>(
                "/api/register",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                },
            );
            console.log(`data register`, data);

            if (data.code === 200) {
                await get().checkToken();
            }
            set({ isLoading: false });
        } catch (error) {
            set({ isLoading: false });
            handleErrorResponse(error, get().loginCount, (n) =>
                set({ loginCount: n }),
            );
        }
    },
    logout: async () => {
        set({ isLoading: true });
        try {
            const { data } = await axios.get<LogoutResponse>("/api/logout");
            if (data.code === 200) {
                set({ isAuthenticated: false });
                queryClient.clear();
                localStorage.removeItem("popupShownAfterLogin");
            }
            set({ isLoading: false });
        } catch (error) {
            handleErrorResponse(error, get().loginCount, (n) =>
                set({ loginCount: n }),
            );
            set({ isLoading: false });
        }
    },
    handleRegistered: () => set({ isRegistered: true }),
    setRegistered: (value) => set({ isRegistered: value }),
    handleAuthenticated: (value) => set({ isAuthenticated: value }),
    resetLoginCount: () => {
        set({ loginCount: 0 });
        localStorage.removeItem("loginCount");
    },
}));
